// From NgRx Signals PR: https://github.com/ngrx/platform/pull/5151

import { computed, untracked, ValueEqualityFn, WritableSignal } from '@angular/core';

export type DelegatedSignalConfig<T> = {
    source: () => T;
    update: (value: NoInfer<T>) => void;
    equal?: ValueEqualityFn<NoInfer<T>>;
};

/**
 * @description
 *
 * Creates a `WritableSignal` whose reads are delegated to a source computation
 * and whose writes are delegated to an `update` callback.
 *
 * Basically, you can read and write from a store and this just works like a `linkedSignal` that syncs back to the store,
 * but you read the signal through this delegated signal.
 *
 * @description
 *
 * Once we update to the latest Angular, replace with `linkedSignal`'s `set` option
 *
 * @link https://github.com/michael-small/signal-forms-experimenting/blob/main/src/app/forms/full-crud/full-crud.ts#L123
 *
 * @example
 *
 * ```ts
 * import { Component, inject } from '@angular/core';
 * import {
 *   delegatedSignal,
 *   patchState,
 *   signalStore,
 *   withMethods,
 *   withState,
 * } from '@ngrx/signals';
 *
 * const CounterStore = signalStore(
 *   withState({ count: 0 }),
 *   withMethods((store) => ({
 *     updateCount(count: number): void {
 *       patchState(store, { count });
 *     },
 *   })),
 * );
 *
 * \@Component({
 *   selector: 'app-counter',
 *   template: '...',
 *   providers: [CounterStore],
 * })
 * class Counter {
 *   readonly #store = inject(CounterStore);
 *
 *   readonly count = delegatedSignal({
 *     source: this.#store.count,
 *     update: this.#store.updateCount,
 *   });
 * }
 * ```
 */
export function delegatedSignal<T>(config: DelegatedSignalConfig<T>): WritableSignal<T> {
    const source = computed(config.source, { equal: config.equal });
    const delegated = computed(source) as WritableSignal<T>;

    delegated.set = value => config.update(value);
    delegated.update = updater => config.update(updater(untracked(source)));
    delegated.asReadonly = () => source;

    return delegated;
}
